/** @jsxImportSource @gasman/smush */

import './position_editor.css';

import { Component } from '@gasman/smush';
import { NumberGrid } from '../widgets/grid';
var removeChildren = require('remove-children');

class PositionEditor extends Component {
    constructor(app, mainView, model) {
        super();
        this.mainView = mainView;
        this.song = null;
        this.undoBuffer = null;
        this.undoPath = null;

        this.container = null;
        this.grid = null;

        this.fields = [];
        this.rowsByFieldName = {};
        this.startIndex = model.fields['positions'].startIndex;
        this.fields = model.fields['positions'].subfield.fieldList;
        this.fields.forEach((field, y) => {
            this.rowsByFieldName[field.name] = y;
        });

        this.columnCount = null;

        this.playbackPosition = null;
        app.player.on('playPosition', (positionNumber) => {
            this.setPlaybackPosition(positionNumber);
        });
        app.player.on('stopPlaying', () => {
            this.setPlaybackPosition(null);
        });
    }

    render(container) {
        this.container = <div class="position-editor"></div>;
        container.appendChild(this.container);
    }

    bind(song, opts) {
        opts = opts || {};

        this.unbind();

        this.song = song;
        this.songLength = song.length;

        let populatedPositionCount = song['positions'].length - this.startIndex;
        this.columnCount = Math.max(populatedPositionCount, song.length) + 1;

        if (opts.undoBuffer) {
            this.undoBuffer = opts.undoBuffer;
            this.undoPath = [...opts.undoPath, 'positions'];
        }

        removeChildren(this.container);
        this.grid = new NumberGrid(this.container, {
            rowLabels: this.fields.map((field) => {
                return field.label;
            }),
            columnLabels: Array.from({ length: this.columnCount }, (x, i) =>
                (i + this.startIndex).toString(),
            ),
            rows: this.fields.length,
            columns: this.columnCount,
            getValue: (x, y) => {
                if (x == this.columnCount - 1) {
                    /* final column is left at '-' */
                    return '-';
                } else {
                    return this.song.getPosition(x + this.startIndex)[
                        this.fields[y].name
                    ];
                }
            },
            commitValue: (x, y, val) => {
                let positionNumber = x + this.startIndex;
                let fieldName = this.fields[y].name;
                let oldValue = this.song.getPosition(positionNumber)[fieldName];

                if (oldValue == val) {
                    if (x == this.columnCount - 1) {
                        /*
                        setting the last (dummy) column;
                        add a new final dummy column, which has the side effect of populating the
                        current one with real data.
                        (Normally onChangePosition would handle this, but that won't trigger when
                        we're setting it to the existing value)
                        */
                        this.extendColumnCount(this.columnCount + 1);
                    }
                    return;
                }
                this.song.setPosition(positionNumber, fieldName, val);

                if (this.undoBuffer) {
                    this.undoBuffer.push({
                        path: this.undoPath,
                        forward: { [positionNumber]: { [fieldName]: val } },
                        reverse: {
                            [positionNumber]: { [fieldName]: oldValue },
                        },
                    });
                }
            },
            ondblclick: (e, x, y) => {
                x += this.startIndex;
                if (this.fields[y].name == 'pattern') {
                    this.mainView.state.patternNumber =
                        this.song.getPosition(x)['pattern'];
                    return false;
                }
            },
        });
        this.grid.addColumnClass(this.columnCount - 1, 'unused');
        song.on('changePosition', this.onChangePosition);
        song.on('changeLength', this.onChangeLength);
    }

    unbind() {
        if (this.song) {
            this.song.removeListener('changePosition', this.onChangePosition);
            this.song.removeListener('changeLength', this.onChangeLength);
            this.song = null;
            this.undoBuffer = null;
            this.undoPath = null;
        }
    }

    onChangePosition = (positionNumber, fieldName, value) => {
        let columnIndex = positionNumber - this.startIndex;
        if (columnIndex < this.columnCount - 1) {
            /* we already have a populated grid column for this position,
            so just update the single cell */
            this.grid.setValue(
                columnIndex,
                this.rowsByFieldName[fieldName],
                value,
            );
        } else {
            /* expand the grid to cover this position (with one dummy column after it) */
            this.extendColumnCount(columnIndex + 2);
        }
    };
    onChangeLength = (newLength) => {
        let oldLength = this.songLength;
        this.songLength = newLength;

        if (newLength >= this.columnCount) {
            this.extendColumnCount(newLength + 1);
        }

        if (newLength > oldLength) {
            /* positions in range [oldLength...newLength] are now used */
            for (let col = oldLength; col < newLength; col++) {
                this.grid.removeColumnClass(col, 'unused');
            }
        } else {
            /* positions in range [newLength...oldLength] are now unused */
            for (let col = newLength; col < oldLength; col++) {
                this.grid.addColumnClass(col, 'unused');
            }
        }
    };

    refreshColumn(columnIndex) {
        this.fields.forEach((field, y) => {
            this.grid.setValue(
                columnIndex,
                y,
                this.song.getPosition(columnIndex + this.startIndex)[
                    field.name
                ],
            );
        });
    }

    extendColumnCount(newColumnCount) {
        let oldColumnCount = this.columnCount;
        this.columnCount = newColumnCount;

        /* fill in the current final (dummy) column with real data */
        this.refreshColumn(oldColumnCount - 1);

        /* append as many new columns as needed, and let the grid's getValue callback
        deal with filling them with real values or '-' */
        for (let i = oldColumnCount; i < newColumnCount; i++) {
            this.grid.appendColumn({ label: (i + this.startIndex).toString() });
            this.grid.addColumnClass(i, 'unused');
        }
    }

    setPlaybackPosition(newPosition) {
        if (this.playbackPosition !== null) {
            if (this.playbackPosition === newPosition) return;
            this.grid.removeColumnClass(
                this.playbackPosition - this.startIndex,
                'playback-position',
            );
        }
        this.playbackPosition = newPosition;
        if (newPosition !== null) {
            this.grid.addColumnClass(
                newPosition - this.startIndex,
                'playback-position',
            );
        }
    }
}

export { PositionEditor };
